/**
 * publicationSnapshot — turn one project into the frozen snapshot a classroom publication serves
 * (`publications/{classroomId}/sketches/{projectId}`). Pure: no Firebase, no DOM.
 *
 * The snapshot is deliberately arSession-SHAPED (same `code` / `width` / `height` / `webgl` /
 * `p5Version` / `helperScripts` / `arSettings` / `sliderValues` fields) so the AR viewer applies it
 * through exactly the same code path as a live session doc. It drops what only a LIVE session needs:
 * `ownerId` (nothing writes back — the published app is read-only and unauthenticated) and `runId`
 * (a snapshot never re-runs; switching sketches in the viewer is what replaces it).
 *
 * The writer adds `snapshotAt: serverTimestamp()`.
 */

import { buildArSessionPayload } from './arSessionPayload';
import {
  AR_SETTINGS_DEFAULTS,
  normalizeArSettings
} from '../../common/types/arSettings';

const AR_SETTINGS_KEYS = Object.keys(AR_SETTINGS_DEFAULTS);

/** Keep only known ARSettings fields — old session docs still carry retired ones (cadence, filter…). */
function pickSettings(source) {
  const picked = {};
  if (!source || typeof source !== 'object') return picked;
  AR_SETTINGS_KEYS.forEach((key) => {
    if (source[key] !== undefined) picked[key] = source[key];
  });
  return picked;
}

/**
 * Decide which AR settings a published sketch runs under.
 *
 * Precedence (lowest → highest): defaults, the publishing instructor's own preferences, the settings
 * the STUDENT last used on their phone (read off their `arSessions/{arSessionId}` doc — they tuned
 * those for this sketch, so they beat the instructor's), then an explicit per-entry override from the
 * publish modal.
 *
 * `showGuiInAr` is always forced off: the lil-gui panel is an editing tool, and the published app has
 * no login with which to establish ownership over it. `frameMode`/`northRef`/`alignSource` are pinned
 * by `normalizeArSettings` for the same reason they left the settings tab — a session doc written
 * before then can still carry `frameMode: 'session'`, and inheriting it would publish a sketch into a
 * frame nobody chose.
 *
 * @param {object|null} studentSettings arSettings from the student's own AR session
 * @param {object|null} instructorSettings the publishing instructor's preferences
 * @param {object|null} [override] per-entry override chosen in the publish modal
 * @returns {import('../../common/types/arSettings').ARSettings}
 */
export function resolveEntrySettings(
  studentSettings,
  instructorSettings,
  override
) {
  return {
    ...normalizeArSettings({
      ...pickSettings(instructorSettings),
      ...pickSettings(studentSettings),
      ...pickSettings(override)
    }),
    showGuiInAr: false
  };
}

/**
 * The stored snapshot body (minus `snapshotAt`, which the writer adds). Same field set as a live
 * session doc's payload minus `ownerId`/`runId` — that's what lets the viewer run both through one
 * code path.
 *
 * @typedef {object} PublicationSnapshot
 * @property {string} code the instance-factory source the viewer evals
 * @property {number|null} width
 * @property {number|null} height
 * @property {boolean} webgl
 * @property {string} p5Version
 * @property {Array<{name: string, code: string}>} helperScripts
 * @property {import('../../common/types/arSettings').ARSettings} arSettings
 * @property {Record<string, unknown>} sliderValues
 */

/**
 * Build the frozen snapshot body for one project.
 *
 * @param {object} args
 * @param {Array} args.files the project's ProjectFile[]
 * @param {import('../../common/types/arSettings').ARSettings} args.arSettings the already-resolved
 *   settings for this entry (see resolveEntrySettings)
 * @param {Record<string, unknown>} [args.sliderValues] slider values frozen from the student's
 *   session, so the published sketch looks the way they left it
 * @returns {PublicationSnapshot}
 */
export function buildPublicationSnapshot({ files, arSettings, sliderValues }) {
  // Re-resolve so a caller passing raw/legacy settings still gets a clean, GUI-free ARSettings.
  const settings = resolveEntrySettings(arSettings, null);

  const {
    code,
    width,
    height,
    webgl,
    p5Version,
    helperScripts
  } = buildArSessionPayload({
    files,
    ownerId: null,
    prevRunId: 0,
    stripOrbitControl: settings.stripOrbitControl,
    stripBackground: settings.stripBackground
  });

  return {
    code,
    width,
    height,
    webgl,
    p5Version,
    helperScripts,
    arSettings: settings,
    sliderValues: sliderValues || {}
  };
}
